import { S, hasGame } from '../core/game';
import { getLang } from '../i18n';

/**
 * Game calendar. Each game week is the Friday, Saturday and Sunday of one real
 * week, starting on Friday 4 September 2026 (the first week of the Taiwanese
 * school year). `S().dayIndex` already counts every day played (it is never
 * reset), so it is the game day number: 0 = the first Friday.
 */
export const START = { y: 2026, m: 9, d: 4 } as const;

/** The game day number (0 = the first Friday). */
export function dayNum(): number {
  return hasGame() ? Math.max(0, S().dayIndex) : 0;
}

/** Real date of game day `k` (local time, midnight). */
export function dateOf(k: number = dayNum()): Date {
  const week = Math.floor(k / 3);
  const off = ((k % 3) + 3) % 3;
  return new Date(START.y, START.m - 1, START.d + week * 7 + off);
}

/** Month 1–12 of game day `k`. */
export function monthOf(k: number = dayNum()): number {
  return dateOf(k).getMonth() + 1;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(opts: Intl.DateTimeFormatOptions, key: string): Intl.DateTimeFormat {
  const lang = getLang();
  const k = `${lang}|${key}`;
  let f = fmtCache.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat(lang, opts);
    fmtCache.set(k, f);
  }
  return f;
}

/** Short localized label, e.g. "Fri, Sep 4" / "9月4日週五" / "vie, 4 sept". */
export function dateLabel(k: number = dayNum()): string {
  return fmt({ weekday: 'short', month: 'short', day: 'numeric' }, 'short').format(dateOf(k));
}

/** Long label for dialogs, e.g. "Friday, September 4". */
export function dateLong(k: number = dayNum()): string {
  return fmt({ weekday: 'long', month: 'long', day: 'numeric' }, 'long').format(dateOf(k));
}

/** English label (always English: used for the speaking practice lines). */
export function dateEnglish(k: number = dayNum()): string {
  return new Intl.DateTimeFormat('en', { weekday: 'long', month: 'long', day: 'numeric' }).format(dateOf(k));
}
